import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CLIENT_RESOURCE_NAMES,
  deleteClientResource,
  downloadClientResourceFile,
  getAllClientDomains,
  getClientById,
  getClientResource,
  getClientResourceUrl,
} from "../../../../../core/services/client.service";
import { getAllClientRoles } from "../../../../../core/services/clientRole.service";
import { getUsersByClient } from "../../../../../core/services/keycloakApi";
import { getUsersByClientId } from "../../../../../core/services/profile.service";
import { extractApiErrorMessage } from "../../../../../core/utils/apiErrorUtils";
import { useAuth } from "../../../../../app/context/AuthContext";
import {
  getAuthenticatedUserFromStorage,
  isAdminUser,
  buildCreateClientUsersPayload,
} from "../../../../../core/utils/util";
import { getClientId } from "../../../../../core/utils/clientDisplay";
import {
  EMPTY_FORM,
  EMPTY_EXISTING_RESOURCES,
  WIZARD_STEPS,
  buildClientFormFromTarget,
  normalizeApiUser,
  getUserUsername,
  isCreatorUser,
  parseClientUsersRoleMap,
  isImageFileName,
} from "../shared";
import {
  buildCompleteThemeConfig,
  parseThemeConfig,
  removeThemeConfigEntries,
} from "../../../../../core/utils/themeConfig";
import {
  CLIENT_KNOWLEDGE_XLSX_ERROR,
  hasProjectKnowledgeXlsxBlockedFile,
  MEETING_NOTES_XLSX_ERROR,
  OTHER_DOCUMENTS_XLSX_ERROR,
} from "../../../../../core/services/fileUpload.service";

const RESOURCE_FIELD_BY_NAME = {
  [CLIENT_RESOURCE_NAMES.KNOWLEDGE_BANK]: "knowledgeBank",
  [CLIENT_RESOURCE_NAMES.LOGO]: "logo",
  [CLIENT_RESOURCE_NAMES.MEETING]: "meeting",
  [CLIENT_RESOURCE_NAMES.OTHER_DOCUMENTS]: "otherDocuments",
  [CLIENT_RESOURCE_NAMES.THEME]: "theme",
};

const loadExistingResourceEntry = async (clientId, fileName, resourceName) => {
  const name = String(fileName ?? "").trim();
  if (!name) return null;

  const id = `${resourceName}:${name}`;
  const entry = { id, name, url: null, resourceName };

  try {
    const blob = await getClientResource(clientId, name, resourceName);
    if (!(blob instanceof Blob) || blob.size === 0) return entry;

    const treatAsImage =
      isImageFileName(name) || Boolean(blob.type?.startsWith("image/"));

    if (treatAsImage) {
      return {
        ...entry,
        url: URL.createObjectURL(blob),
        revokeOnCleanup: true,
      };
    }

    // Non-image resources still go through the view API so the file is confirmed.
    return entry;
  } catch {
    // Fall back to cached data URL for images when the blob request fails.
  }

  if (isImageFileName(name)) {
    try {
      const url = await getClientResourceUrl(clientId, name, resourceName);
      if (url) return { ...entry, url, revokeOnCleanup: false };
    } catch {
      // Keep filename-only entry when preview fetch fails.
    }
  }

  return entry;
};

const loadExistingResourceEntries = async (clientId, fileNames, resourceName) => {
  const names = Array.isArray(fileNames) ? fileNames : [];
  const entries = await Promise.all(
    names.map((fileName) =>
      loadExistingResourceEntry(clientId, fileName, resourceName),
    ),
  );
  return entries.filter(Boolean);
};

const getThemeNames = (detail) =>
  detail?.themeNames?.length ? detail.themeNames : detail?.mediaFiles?.theme ?? [];

export default function useCreateClientModal({
  open,
  onClose,
  onSubmit,
  mode = "create",
  editTarget = null,
  defaultStep = null,
}) {
  const isEditMode = mode === "edit";
  const { userInfo } = useAuth();
  const isAdmin = isAdminUser(userInfo);
  const creatorUser = useMemo(
    () => getAuthenticatedUserFromStorage() || userInfo,
    [userInfo],
  );
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [clientRoles, setClientRoles] = useState([]);
  const [clientRolesLoading, setClientRolesLoading] = useState(false);
  const [userAssignments, setUserAssignments] = useState([]);
  const [existingResources, setExistingResources] = useState(EMPTY_EXISTING_RESOURCES);
  const [existingResourcesLoading, setExistingResourcesLoading] = useState(false);
  const [deletingThemeNames, setDeletingThemeNames] = useState([]);
  const [deletingResourceKeys, setDeletingResourceKeys] = useState([]);
  const [downloadingResourceKeys, setDownloadingResourceKeys] = useState([]);
  const existingObjectUrlsRef = useRef([]);
  const contentRef = useRef(null);

  const canGoPrevious = activeStep > 0;
  const canGoNext = activeStep < WIZARD_STEPS.length - 1;
  const canSubmit = Boolean(form.clientName.trim());
  const editClientId = isEditMode ? getClientId(editTarget) : null;

  const revokeExistingObjectUrls = useCallback(() => {
    existingObjectUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // Ignore revoke failures.
      }
    });
    existingObjectUrlsRef.current = [];
  }, []);

  // Swaps a set of resource entries for another, revoking the previews that are
  // going away so the cleanup ref only tracks urls that are still rendered.
  const swapTrackedObjectUrls = useCallback((staleEntries, nextEntries) => {
    const staleUrls = new Set(
      (staleEntries ?? [])
        .filter((entry) => entry?.revokeOnCleanup && entry.url)
        .map((entry) => entry.url),
    );

    staleUrls.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // Ignore revoke failures.
      }
    });

    const nextUrls = (nextEntries ?? [])
      .filter((entry) => entry?.revokeOnCleanup && entry.url)
      .map((entry) => entry.url);

    existingObjectUrlsRef.current = [
      ...new Set([
        ...existingObjectUrlsRef.current.filter((url) => !staleUrls.has(url)),
        ...nextUrls,
      ]),
    ];
  }, []);

  const handleNextStep = useCallback(() => {
    setActiveStep((step) => Math.min(WIZARD_STEPS.length - 1, step + 1));
  }, []);

  const handlePreviousStep = useCallback(() => {
    setActiveStep((step) => Math.max(0, step - 1));
  }, []);

  const handleStepClick = useCallback((stepIndex) => {
    if (stepIndex < 0 || stepIndex >= WIZARD_STEPS.length) return;
    setActiveStep(stepIndex);
  }, []);

  const handleDialogClose = useCallback((_, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
  }, []);

  useEffect(() => {
    if (open) {
      setForm(isEditMode ? buildClientFormFromTarget(editTarget) : EMPTY_FORM);
      setError("");
      const stepIndex =
        typeof defaultStep === "number" &&
        defaultStep >= 0 &&
        defaultStep < WIZARD_STEPS.length
          ? defaultStep
          : 0;
      setActiveStep(stepIndex);
      setUserAssignments([]);
      setDeletingThemeNames([]);
      setDeletingResourceKeys([]);
      setDownloadingResourceKeys([]);
      if (!isEditMode) {
        revokeExistingObjectUrls();
        setExistingResources(EMPTY_EXISTING_RESOURCES);
        setExistingResourcesLoading(false);
      }
    }
  }, [open, isEditMode, editTarget, defaultStep, revokeExistingObjectUrls]);

  // Prefill uploaded resources (knowledge bank, logo, meeting, other docs, theme) when editing.
  useEffect(() => {
    if (!open || !isEditMode || !editClientId) {
      return undefined;
    }

    let cancelled = false;

    const loadExisting = async () => {
      setExistingResourcesLoading(true);
      revokeExistingObjectUrls();

      try {
        const detail = await getClientById(editClientId);
        if (cancelled) return;

        const mediaFiles = detail?.mediaFiles ?? {};
        const fileNamesByField = {
          knowledgeBank: mediaFiles.knowledgeBank ?? [],
          logo: mediaFiles.logo ?? [],
          meeting: mediaFiles.meeting ?? [],
          otherDocuments: mediaFiles.otherDocuments ?? [],
          theme: getThemeNames(detail),
        };

        const next = { ...EMPTY_EXISTING_RESOURCES };
        const objectUrls = [];

        await Promise.all(
          Object.entries(RESOURCE_FIELD_BY_NAME).map(async ([resourceName, field]) => {
            next[field] = await loadExistingResourceEntries(
              editClientId,
              fileNamesByField[field],
              resourceName,
            );
            next[field].forEach((entry) => {
              if (entry.revokeOnCleanup && entry.url) objectUrls.push(entry.url);
            });
          }),
        );

        if (cancelled) {
          objectUrls.forEach((url) => URL.revokeObjectURL(url));
          return;
        }

        existingObjectUrlsRef.current = objectUrls;
        setExistingResources(next);
        setForm((prev) => ({
          ...prev,
          themeConfig: parseThemeConfig(
            detail?.themeConfig ?? detail?.theme_config,
          ),
        }));
      } catch {
        if (!cancelled) {
          revokeExistingObjectUrls();
          setExistingResources(EMPTY_EXISTING_RESOURCES);
        }
      } finally {
        if (!cancelled) setExistingResourcesLoading(false);
      }
    };

    loadExisting();

    return () => {
      cancelled = true;
    };
  }, [open, isEditMode, editClientId, revokeExistingObjectUrls]);

  useEffect(
    () => () => revokeExistingObjectUrls(),
    [revokeExistingObjectUrls],
  );

  // Re-reads the persisted theme list so the gallery matches the server instead
  // of trusting the delete response, which does not always echo the new list.
  const refreshExistingThemes = useCallback(async () => {
    if (!editClientId) return;

    const detail = await getClientById(editClientId);
    const entries = await loadExistingResourceEntries(
      editClientId,
      getThemeNames(detail),
      CLIENT_RESOURCE_NAMES.THEME,
    );

    setExistingResources((prev) => {
      swapTrackedObjectUrls(prev.theme, entries);
      return { ...prev, theme: entries };
    });
  }, [editClientId, swapTrackedObjectUrls]);

  // Other surfaces (the live background slideshow) listen for this to reload.
  const notifyThemesChanged = useCallback(() => {
    window.dispatchEvent(new Event("client-theme-updated"));
  }, []);

  const syncThemesAfterDelete = useCallback(
    async (deletedNames) => {
      const removed = new Set(deletedNames.map(String));

      setExistingResources((prev) => {
        const gone = prev.theme.filter((entry) => removed.has(entry.name));
        if (!gone.length) return prev;
        swapTrackedObjectUrls(gone, []);
        return {
          ...prev,
          theme: prev.theme.filter((entry) => !removed.has(entry.name)),
        };
      });

      setForm((prev) => ({
        ...prev,
        themeConfig: removeThemeConfigEntries(prev.themeConfig, deletedNames),
      }));

      notifyThemesChanged();

      try {
        await refreshExistingThemes();
      } catch {
        // The delete already succeeded, so keep the locally trimmed list.
      }
    },
    [notifyThemesChanged, refreshExistingThemes, swapTrackedObjectUrls],
  );

  const handleRemoveExistingResource = useCallback(
    async (fileName, resourceName) => {
      const name = String(fileName ?? "").trim();
      const resource = String(resourceName ?? "").trim();
      const field = RESOURCE_FIELD_BY_NAME[resource];
      const key = `${resource}:${name}`;
      if (!editClientId || !name || !field || deletingResourceKeys.includes(key)) {
        return;
      }

      setDeletingResourceKeys((prev) =>
        prev.includes(key) ? prev : [...prev, key],
      );

      try {
        await deleteClientResource(editClientId, [name], resource);
        setError("");

        if (resource === CLIENT_RESOURCE_NAMES.THEME) {
          await syncThemesAfterDelete([name]);
          return;
        }

        setExistingResources((prev) => {
          const list = prev[field] ?? [];
          const removed = list.filter((entry) => entry.name === name);
          swapTrackedObjectUrls(removed, []);
          return {
            ...prev,
            [field]: list.filter((entry) => entry.name !== name),
          };
        });
      } catch (err) {
        setError(
          extractApiErrorMessage(
            err?.response?.data,
            err?.message || `Failed to delete "${name}".`,
          ),
        );
      } finally {
        setDeletingResourceKeys((prev) => prev.filter((item) => item !== key));
      }
    },
    [
      deletingResourceKeys,
      editClientId,
      swapTrackedObjectUrls,
      syncThemesAfterDelete,
    ],
  );

  const handleDownloadExistingResource = useCallback(
    async (fileName, resourceName) => {
      const name = String(fileName ?? "").trim();
      const resource = String(resourceName ?? "").trim();
      const key = `${resource}:${name}`;
      if (
        !editClientId ||
        !name ||
        !resource ||
        downloadingResourceKeys.includes(key)
      ) {
        return;
      }

      setDownloadingResourceKeys((prev) =>
        prev.includes(key) ? prev : [...prev, key],
      );

      try {
        await downloadClientResourceFile(editClientId, name, resource);
        setError("");
      } catch {
        setError(`Could not download "${name}". Please try again.`);
      } finally {
        setDownloadingResourceKeys((prev) => prev.filter((item) => item !== key));
      }
    },
    [downloadingResourceKeys, editClientId],
  );

  const handleRemoveExistingTheme = useCallback(
    async (themeName) => {
      const name = String(themeName ?? "").trim();
      if (!editClientId || !name || deletingThemeNames.includes(name)) return;

      setDeletingThemeNames((prev) =>
        prev.includes(name) ? prev : [...prev, name],
      );

      try {
        await deleteClientResource(
          editClientId,
          [name],
          CLIENT_RESOURCE_NAMES.THEME,
        );
        setError("");
        await syncThemesAfterDelete([name]);
      } catch (err) {
        setError(
          extractApiErrorMessage(
            err?.response?.data,
            err?.message || `Failed to delete "${name}".`,
          ),
        );
      } finally {
        setDeletingThemeNames((prev) => prev.filter((item) => item !== name));
      }
    },
    [deletingThemeNames, editClientId, syncThemesAfterDelete],
  );

  const handleRemoveAllExistingThemes = useCallback(async () => {
    if (!editClientId || deletingThemeNames.length) return;

    const names = existingResources.theme
      .map((entry) => String(entry.name ?? "").trim())
      .filter(Boolean);
    if (!names.length) return;

    setDeletingThemeNames(names);

    try {
      await deleteClientResource(
        editClientId,
        names,
        CLIENT_RESOURCE_NAMES.THEME,
      );
      setError("");
      await syncThemesAfterDelete(names);
    } catch (err) {
      setError(
        extractApiErrorMessage(
          err?.response?.data,
          err?.message || "Failed to delete the backgrounds.",
        ),
      );
    } finally {
      setDeletingThemeNames([]);
    }
  }, [
    deletingThemeNames.length,
    editClientId,
    existingResources.theme,
    syncThemesAfterDelete,
  ]);


  const handleSelectedUsersChange = useCallback((selectedUsers) => {
    const filteredSelected = (selectedUsers || []).filter(
      (user) => !isCreatorUser(user, creatorUser),
    );

    setUserAssignments((prev) => {
      const selectedIds = new Set(
        filteredSelected.map((user) => String(user.uid)),
      );
      const kept = prev.filter((entry) => selectedIds.has(String(entry.userId)));
      const keptIds = new Set(kept.map((entry) => String(entry.userId)));
      const added = filteredSelected
        .filter((user) => !keptIds.has(String(user.uid)))
        .map((user) => ({ userId: user.uid, roles: [] }));

      return [...kept, ...added];
    });
  }, [creatorUser]);

  const handleRemoveUser = useCallback((userId) => {
    setUserAssignments((prev) =>
      prev.filter((entry) => String(entry.userId) !== String(userId)),
    );
  }, []);

  const handleUpdateUserRoles = useCallback((userId, roles) => {
    setUserAssignments((prev) =>
      prev.map((entry) =>
        String(entry.userId) === String(userId) ? { ...entry, roles } : entry,
      ),
    );
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const frameId = requestAnimationFrame(() => {
      contentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    });

    return () => cancelAnimationFrame(frameId);
  }, [activeStep, open]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setDomainsLoading(true);

    getAllClientDomains()
      .then((list) => {
        if (cancelled) return;
        setDomains(list);

        // In edit mode, resolve domainId from the existing domain name so
        // submit does not treat the unchanged domain as a new one.
        setForm((prev) => {
          const name = String(prev.domainName ?? "").trim();
          if (!name || prev.domainId != null) return prev;
          const match = list.find(
            (d) =>
              String(d.domainName ?? "").toLowerCase() === name.toLowerCase(),
          );
          if (!match?.domainId) return prev;
          return { ...prev, domainId: match.domainId };
        });
      })
      .catch(() => {
        if (!cancelled) setDomains([]);
      })
      .finally(() => {
        if (!cancelled) setDomainsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setUsersLoading(true);

    getUsersByClient()
      .then(async (response) => {
        if (cancelled) return;
        const data = response?.data;
        const apiUsers = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const normalizedUsers = apiUsers
          .map(normalizeApiUser)
          .filter(Boolean)
          .filter((user) => user.enabled !== false);

        setAllUsers(normalizedUsers);
      })
      .catch(() => {
        if (!cancelled) {
          setAllUsers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setClientRolesLoading(true);

    getAllClientRoles()
      .then((list) => {
        if (!cancelled) setClientRoles(list);
      })
      .catch(() => {
        if (!cancelled) setClientRoles([]);
      })
      .finally(() => {
        if (!cancelled) setClientRolesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Prefill Assign Users & Roles when editing an existing client.
  useEffect(() => {
    if (!open || !isEditMode || usersLoading || !allUsers.length) {
      return undefined;
    }

    const clientId = getClientId(editTarget);
    if (!clientId) return undefined;

    let cancelled = false;

    const hydrateAssignments = async () => {
      let roleByUsername = new Map();
      let usernames = [];

      try {
        const detail = await getClientById(clientId);
        if (cancelled) return;
        roleByUsername = parseClientUsersRoleMap(
          detail?.users ?? editTarget?.users,
        );
      } catch {
        roleByUsername = parseClientUsersRoleMap(editTarget?.users);
      }

      try {
        usernames = await getUsersByClientId(clientId);
      } catch {
        usernames = [];
      }

      if (cancelled) return;

      const usernameSet = new Set(
        [
          ...(Array.isArray(usernames) ? usernames : []),
          ...roleByUsername.keys(),
        ]
          .map((name) => String(name ?? "").trim().toLowerCase())
          .filter(Boolean),
      );

      if (!usernameSet.size) {
        setUserAssignments([]);
        return;
      }

      const userByUsername = new Map();
      allUsers.forEach((user) => {
        const username = getUserUsername(user).trim().toLowerCase();
        if (username) userByUsername.set(username, user);
      });

      const assignments = [];
      usernameSet.forEach((username) => {
        const user = userByUsername.get(username);
        if (!user || isCreatorUser(user, creatorUser)) return;

        assignments.push({
          userId: user.uid,
          roles: roleByUsername.get(username) || [],
        });
      });

      setUserAssignments(assignments);
    };

    hydrateAssignments();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    isEditMode,
    editTarget,
    allUsers,
    usersLoading,
    creatorUser,
  ]);

  const handleSubmit = async () => {
    const clientName = form.clientName.trim();
    const domainName = form.domainName.trim();

    if (!clientName) {
      setError("Client name is required.");
      return;
    }

    if (hasProjectKnowledgeXlsxBlockedFile(form.knowledgeBank)) {
      setError(CLIENT_KNOWLEDGE_XLSX_ERROR);
      return;
    }

    if (hasProjectKnowledgeXlsxBlockedFile(form.meeting)) {
      setError(MEETING_NOTES_XLSX_ERROR);
      return;
    }

    if (hasProjectKnowledgeXlsxBlockedFile(form.otherDocuments)) {
      setError(OTHER_DOCUMENTS_XLSX_ERROR);
      return;
    }

    setError("");
    try {
      const authUser = creatorUser;
      const users = buildCreateClientUsersPayload({
        userAssignments,
        allUsers,
        creatorUser: authUser,
        clientRoles,
        isAdmin,
      });

      const payload = {
        clientName,
        domainName,
        domainId: form.domainId,
        knowledgeBank: form.knowledgeBank,
        logo: form.logo,
        theme: form.theme,
        themeConfig: buildCompleteThemeConfig(form.themeConfig, [
          ...(isEditMode
            ? existingResources.theme.map((entry) => entry.name)
            : []),
          ...form.theme.map((file) => file.name),
        ]),
        meeting: form.meeting,
        otherDocuments: form.otherDocuments,
        users,
      };

      if (isEditMode) {
        const clientId =
          editTarget?.clientId ?? editTarget?.Id ?? editTarget?.id ?? null;
        if (!clientId) {
          setError("Client id is missing.");
          return;
        }
        await onSubmit({ ...payload, clientId });
      } else {
        await onSubmit(payload);
      }

      setForm(EMPTY_FORM);
      setUserAssignments([]);
      onClose();
    } catch {
      // Keep panel open on error.
    }
  };

  return {
    isEditMode,
    isAdmin,
    creatorUser,
    form,
    setForm,
    error,
    domains,
    domainsLoading,
    activeStep,
    allUsers,
    usersLoading,
    clientRoles,
    clientRolesLoading,
    userAssignments,
    existingResources,
    existingResourcesLoading,
    deletingThemeNames,
    deletingResourceKeys,
    downloadingResourceKeys,
    contentRef,
    canGoPrevious,
    canGoNext,
    canSubmit,
    handleNextStep,
    handlePreviousStep,
    handleStepClick,
    handleDialogClose,
    handleSelectedUsersChange,
    handleRemoveUser,
    handleUpdateUserRoles,
    handleRemoveExistingResource,
    handleDownloadExistingResource,
    handleRemoveExistingTheme,
    handleRemoveAllExistingThemes,
    handleSubmit,
  };
}
